/**
 * DraftingWizard - multi-step creation wizard for drafts & strategies.
 *
 * Follows the MatterWizard pattern: each step is a BaseForm with its own
 * schema, the wizard accumulates data across steps, and submits on the
 * final step. Uses only global shared components (Input, Textarea,
 * Dropdown, FileUploader, Button) - no one-off styled primitives.
 *
 * Steps:
 *   1. Type selection   - visual card grid + search/filter (custom component inside BaseForm)
 *   2. Details          - name, description, tone, custom instructions (all shared inputs)
 *   3. Context & launch - file uploads (FileUploader) + generation options
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Spinner from '../shared/TailwindComponents/Spinner';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';
import {
  PiFileTextLight,
  PiLightbulbLight,
  PiScalesLight,
  PiListChecksLight,
  PiFileDocLight,
  PiChatTeardropDotsLight,
  PiRocketLaunchLight,
  PiStackLight,
} from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import BaseForm, { BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents/BaseForm';
import WizardStepRail from '../shared/FormComponents/WizardStepRail';
import { Well } from '../shared/brand/Field';
import { FOCUS_RING } from '../shared/TailwindComponents/FieldShell';
import { selectedCardClass } from '../shared/brand/selectedCard';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import {
  createDrafting,
  generateDrafting,
  fetchDraftingTypes,
  DraftingKind,
  MatterContextOptions,
} from '../../store/slices/draftingSlice';
import apis from '../../utils/apis';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import ChooseTemplateStep, { ChooseTemplateSelection } from './wizard/ChooseTemplateStep';
import TieToMatterStep, { MatterOption } from './wizard/TieToMatterStep';
import MatterContextStep, { DEFAULT_MATTER_CONTEXT_OPTIONS } from './wizard/MatterContextStep';
import { readErrorMessage } from '../../utils/errorMessage';

// ─── Constants ────────────────────────────────────────────────────────────────

const TONE_OPTIONS = [
  { value: '', label: 'Default (Professional)' },
  { value: 'formal', label: 'Formal & Authoritative' },
  { value: 'persuasive', label: 'Persuasive & Compelling' },
  { value: 'neutral', label: 'Neutral & Objective' },
  { value: 'concise', label: 'Concise & Direct' },
  { value: 'detailed', label: 'Detailed & Comprehensive' },
  { value: 'empathetic', label: 'Empathetic & Client-Focused' },
];

type WizardStep = 'kind' | 'choose' | 'matter' | 'matter_context' | 'details' | 'context';

const ALL_STEPS: { key: WizardStep; label: string; icon: React.ReactNode }[] = [
  { key: 'kind',           label: 'Type',           icon: <PiStackLight className="w-4 h-4" /> },
  { key: 'choose',         label: 'Template',       icon: <PiFileDocLight className="w-4 h-4" /> },
  { key: 'matter',         label: 'Matter',         icon: <PiScalesLight className="w-4 h-4" /> },
  { key: 'matter_context', label: 'Matter context', icon: <PiListChecksLight className="w-4 h-4" /> },
  { key: 'details',        label: 'Details',        icon: <PiChatTeardropDotsLight className="w-4 h-4" /> },
  { key: 'context',        label: 'Launch',         icon: <PiRocketLaunchLight className="w-4 h-4" /> },
];

// ─── Schemas ──────────────────────────────────────────────────────────────────

// Step 1 (choose): document_type_id is filled in by the ChooseTemplateStep
// component, so no required field on the BaseForm schema itself - we gate
// "Next" via a custom validator below.
const chooseSchema = FormSchema.create('ChooseStep', {});

// Step 0 (kind): document vs strategy - chosen via cards, gated by submitDisabled.
const kindSchema = FormSchema.create('KindStep', {});

// Step 2 (matter) - optional
const matterSchema = FormSchema.create('MatterStep', {});
const matterContextSchema = FormSchema.create('MatterContextStep', {});

// Step 5 (details): name optional, all others optional
const detailsSchema = FormSchema.create('DetailsStep', {
  name:         new FormField('name',         'title', false, 'Document name',   { maxLength: 200 }),
  description:  new FormField('description',  'body',  false, 'Description',     { maxLength: 2000 }),
  tone:         new FormField('tone',         'title', false, 'Tone',            { maxLength: 100 }),
  instructions: new FormField('instructions', 'body',  false, 'Instructions',    { maxLength: 3000 }),
  additional_context: new FormField('additional_context', 'body', false, 'Additional context', { maxLength: 3000 }),
});

// Step 6: no required fields - context files are optional
const contextSchema = FormSchema.create('ContextStep', {});

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DraftingWizardProps {
  onCreated?: (uuid: string) => void;
  onCancel: () => void;
  /** Pre-bind the new draft to this matter (e.g. when launched from a matter's
   * Drafts panel). The matter step opens pre-selected. */
  initialMatterUuid?: string | null;
}
/** Pinned so the canvas cannot resize as the user moves between steps. */
const WIZARD_MAX_WIDTH = 'max-w-3xl';

// ─── Accumulated data type ─────────────────────────────────────────────────────

interface WizardData {
  document_type_id: string;
  /** Selected structure template id (own or PONS). Empty string = none. */
  structure_template_id: string;
  /** Bound matter uuid (or empty for standalone). */
  matter_uuid: string;
  /** Selected matter's jurisdiction - drives the cases toggle. */
  matter_jurisdiction: string | null;
  /** Per-section gates honored when a matter is bound. */
  matter_context_options: MatterContextOptions;
  name: string;
  description: string;
  tone: string;
  instructions: string;
  additional_context: string;
  contextFiles: File[];
}

// ─── Main component ────────────────────────────────────────────────────────────

const DraftingWizard: React.FC<DraftingWizardProps> = ({ onCreated, onCancel, initialMatterUuid }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  // Kind is chosen in the wizard's first step now (was a prop / modal before).
  const [kind, setKind] = useState<DraftingKind | null>(null);

  const types = useAppSelector((s: RootState) => (kind ? s.drafting.types[kind] : []));
  const typesLoading = useAppSelector((s: RootState) => (kind ? s.drafting.typesLoading[kind] : false));

  useEffect(() => {
    if (kind && types.length === 0 && !typesLoading) {
      dispatch(fetchDraftingTypes({ kind }));
    }
  }, [dispatch, kind, types.length, typesLoading]);

  const [currentStep, setCurrentStep] = useState<WizardStep>('kind');

  // Accumulated data across all steps
  const [data, setData] = useState<WizardData>({
    document_type_id: '',
    structure_template_id: '',
    matter_uuid: initialMatterUuid || '',
    matter_jurisdiction: null,
    matter_context_options: { ...DEFAULT_MATTER_CONTEXT_OPTIONS },
    name: '',
    description: '',
    tone: '',
    instructions: '',
    additional_context: '',
    contextFiles: [],
  });

  // Derive visible steps - matter_context only appears when a matter is bound.
  const STEPS = useMemo(
    () => ALL_STEPS.filter((s) => s.key !== 'matter_context' || !!data.matter_uuid),
    [data.matter_uuid],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Synchronous re-entrancy guard: state updates are async, so a fast
  // double-click could fire two create+generate flows before isSubmitting
  // flips. The ref blocks the second call immediately.
  const submittingRef = useRef(false);
  // Per-file upload progress surfaced in the wizard banner during submit.
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; name: string } | null>(null);

  const kindLabel = kind === 'strategy' ? t('strategy') : t('draft');

  // ── Step 0: kind (Document vs Strategy) ──────────────────────────
  // Switching kind clears any template selection made under the old kind.
  const handleKindChange = useCallback((next: DraftingKind) => {
    setKind((prev) => {
      if (prev !== next) {
        setData((d) => ({ ...d, document_type_id: '', structure_template_id: '' }));
      }
      return next;
    });
  }, []);
  const handleKindStepSubmit = useCallback(() => setCurrentStep('choose'), []);

  // ── Step 1: choose template (merged document-type + own templates) ─
  // submitDisabled on the BaseForm prevents reaching this without a
  // selection, so no extra runtime guard is needed here.
  const handleChooseStepSubmit = useCallback(() => setCurrentStep('matter'), []);

  // ── Step 2: matter ─ already stored on change; advance into context
  // when a matter is bound, otherwise skip directly to details.
  const handleMatterStepSubmit = useCallback(() => {
    setCurrentStep(data.matter_uuid ? 'matter_context' : 'details');
  }, [data.matter_uuid]);

  // ── Step 4: matter_context ─ no-op aside from advancing.
  const handleMatterContextStepSubmit = useCallback(() => setCurrentStep('details'), []);

  // ── Step 5: details ──────────────────────────────────────────────
  const handleDetailsStepSubmit = useCallback(
    (formData: Record<string, any>) => {
      setData((prev) => ({
        ...prev,
        name:               formData.name               ?? prev.name,
        description:        formData.description        ?? prev.description,
        tone:               formData.tone               ?? prev.tone,
        instructions:       formData.instructions       ?? prev.instructions,
        additional_context: formData.additional_context ?? prev.additional_context,
      }));
      setCurrentStep('context');
    },
    []
  );

  // ── Step 3 (final) submit ─────────────────────────────────────────
  const handleContextStepSubmit = useCallback(
    async (formData: Record<string, any>) => {
      // Kind is guaranteed by step 0, but narrow it for the create/generate
      // calls below (and satisfy the type checker).
      if (!kind) return;
      // BaseForm gives us the latest fileUploader contents here - accept
      // them so users don't lose files they added on this step.
      const contextFilesFromForm = Array.isArray(formData.contextFiles)
        ? (formData.contextFiles as File[])
        : data.contextFiles;
      if (contextFilesFromForm !== data.contextFiles) {
        setData((prev) => ({ ...prev, contextFiles: contextFilesFromForm }));
      }
      // Use the locally-resolved value below, not the (possibly stale) `data`
      // closure - the setData call above is asynchronous.
      const contextFiles = contextFilesFromForm;
      // Re-entrancy guard - block a second submit while one is in flight.
      if (submittingRef.current) return;
      submittingRef.current = true;
      setIsSubmitting(true);

      // Build description/instructions merged string
      const toneLabel = TONE_OPTIONS.find((o) => o.value === data.tone)?.label ?? data.tone;
      const customInstructions = [
        data.tone ? `Tone: ${toneLabel}` : '',
        data.instructions.trim(),
      ]
        .filter(Boolean)
        .join('\n\n');

      const fullDescription = [data.description.trim(), customInstructions]
        .filter(Boolean)
        .join('\n\n');

      // Step 1 - create the row, but DON'T auto-generate yet. Attachments
      // need to land in blob storage before the AI handler runs, otherwise
      // they're invisible to the generation pipeline.
      const result = await dispatch(
        createDrafting({
          kind,
          document_type_id: data.document_type_id,
          name: data.name.trim() || undefined,
          description: fullDescription || undefined,
          matter_uuid: data.matter_uuid || undefined,
          structure_template_id: data.structure_template_id || undefined,
          instructions: data.instructions.trim() || undefined,
          tone: data.tone || undefined,
          additional_context: data.additional_context.trim() || undefined,
          matter_context_options: data.matter_uuid ? data.matter_context_options : undefined,
          autoGenerate: false,
        })
      );

      if (createDrafting.fulfilled.match(result) && result.payload?.uuid) {
        const uuid = result.payload.uuid;

        // Step 2 - upload attachments serially. Sequential keeps the
        // progress story simple and avoids burst-load on the blob layer
        // for very large files; the multi-file UX is preserved.
        if (contextFiles.length > 0) {
          for (let i = 0; i < contextFiles.length; i++) {
            const file = contextFiles[i];
            setUploadProgress({ current: i + 1, total: contextFiles.length, name: file.name });
            const form = new FormData();
            form.append('file', file);
            try {
              await apis.post(`/drafting/${kind === 'draft' ? 'drafts' : 'insights'}/${uuid}/files`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
            } catch (err: any) {
              dispatch(showFeedbackModal({
                modalType: 'error',
                message: readErrorMessage(err, t('Failed to upload {{name}} ({{n}} of {{total}})', {
                  name: file.name, n: i + 1, total: contextFiles.length,
                })),
                showModal: true,
                duration: 4000,
              }));
              setUploadProgress(null);
              setIsSubmitting(false);
              submittingRef.current = false;
              return;
            }
          }
          setUploadProgress(null);
        }

        // Step 3 - kick off generation now that attachments are in blob.
        const genResult = await dispatch(generateDrafting({
          kind,
          uuid,
          structure_template_id: data.structure_template_id || undefined,
          instructions: data.instructions.trim() || undefined,
          tone: data.tone || undefined,
          additional_context: data.additional_context.trim() || undefined,
          matter_context_options: data.matter_uuid ? data.matter_context_options : undefined,
        }));

        // Surface generation-enqueue failures instead of silently showing
        // success (the thunk resolves-with-rejection; it never throws).
        if (generateDrafting.rejected.match(genResult)) {
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: (genResult.payload as string)
              || t('{{kind}} created, but generation could not be queued. Open it and try Regenerate.', { kind: kindLabel }),
            showModal: true,
            duration: 4000,
          }));
          setIsSubmitting(false);
          submittingRef.current = false;
          // Still navigate to the created item so the user can retry there.
          if (onCreated) onCreated(uuid); else history.push(`/drafting/${uuid}`);
          return;
        }

        dispatch(
          showFeedbackModal({
            modalType: 'success',
            message: t('{{kind}} created — generation queued.', { kind: kindLabel }),
            showModal: true,
            duration: 3000,
          })
        );
        if (onCreated) {
          onCreated(uuid);
        } else {
          history.push(`/drafting/${uuid}`);
        }
      } else {
        dispatch(
          showFeedbackModal({
            modalType: 'error',
            message: (result.payload as string) || t('Failed to create.'),
            showModal: true,
            duration: 3000,
          })
        );
        setIsSubmitting(false);
        submittingRef.current = false;
      }
    },
    [dispatch, t, kind, kindLabel, data, onCreated, history]
  );

  const handleBack = useCallback(() => {
    // Don't allow navigating away mid-submit (create → upload → generate).
    if (submittingRef.current) return;
    const idx = STEPS.findIndex((s) => s.key === currentStep);
    if (idx <= 0) {
      onCancel();
    } else {
      setCurrentStep(STEPS[idx - 1].key);
    }
  }, [STEPS, currentStep, onCancel]);

  // ── Step 1: Choose template (PONS catalogue OR own structure template) ─
  // The selection is mutually exclusive across the two tabs; see
  // ChooseTemplateStep for the picker UI and the preview wiring.
  const chooseSelection: ChooseTemplateSelection = useMemo(
    () => ({
      document_type_id: data.document_type_id,
      structure_template_id: data.structure_template_id,
      display_name: data.name,
    }),
    [data.document_type_id, data.structure_template_id, data.name],
  );

  // ── Step 0: kind selection (Document vs Strategy) ─────────────────
  const kindSections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        fields: [
          {
            name: 'kind',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: KindStep,
            customProps: { value: kind, onChange: handleKindChange },
          } as BaseFormFieldConfig,
        ],
      },
    ],
    [kind, handleKindChange]
  );

  const chooseSections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        fields: [
          {
            name: 'choose_template',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: ChooseTemplateStep,
            customProps: {
              kind: kind ?? 'draft',
              value: chooseSelection,
              onChange: (next: ChooseTemplateSelection) =>
                setData((prev) => ({
                  ...prev,
                  document_type_id: next.document_type_id,
                  structure_template_id: next.structure_template_id,
                  // Pre-fill the draft name from the picked label, but only
                  // when the user hasn't set one yet - otherwise their edit
                  // would be clobbered on re-selection.
                  name: prev.name || next.display_name || '',
                })),
            },
          } as BaseFormFieldConfig,
        ],
      },
    ],
    [chooseSelection, kind]
  );

  // ── Step 2: Details ───────────────────────────────────────────────
  const detailsSections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        fields: [
          {
            name: 'name',
            type: 'input',
            label: t('Document name'),
            placeholder: t('Untitled draft'),
            /*
              The box arrives PRE-FILLED with the name of the template picked
              two steps back (see `chooseSections`), and that pre-fill is what
              the document ends up called. Saying so is the difference between
              a helpful default and a name the user does not remember choosing
              - which is exactly how it was read. Renaming later is a click on
              the title, so the note also says the choice is not final.
            */
            description: t('Pre-filled from the template you picked. Change it to whatever this document should be called — you can rename it later.'),
            gridSize: 'full',
          } as BaseFormFieldConfig,
          {
            name: 'description',
            type: 'textarea',
            label: t('Description'),
            placeholder: t(
              'Describe what the document should cover, the context, specific outcomes or parties involved…'
            ),
            rows: 4,
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
      {
        title: t('Generation preferences'),
        description: t('Guide the writing style and any specific requirements for the AI.'),
        fields: [
          {
            name: 'tone',
            type: 'dropdown',
            label: t('Tone & style'),
            options: TONE_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) })),
            placeholder: t('Professional (default)'),
            gridSize: 'full',
            customProps: { fullWidth: true, useApi: false },
          } as BaseFormFieldConfig,
          {
            name: 'instructions',
            type: 'textarea',
            label: t('Custom instructions'),
            placeholder: t(
              'Optional: specific sections to include, legal jurisdiction, parties involved, emphasis areas…'
            ),
            rows: 4,
            gridSize: 'full',
          } as BaseFormFieldConfig,
          {
            name: 'additional_context',
            type: 'textarea',
            label: t('Additional context'),
            placeholder: t(
              'Optional: background facts, prior counsel position, client preferences…'
            ),
            rows: 3,
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
    ],
    [t]
  );

  // ── Step 3: Context files ─────────────────────────────────────────
  const contextSections: BaseFormSectionConfig[] = useMemo(
    () => [
      {
        title: t('Context files'),
        description: t(
          'Upload documents the AI should reference — prior drafts, briefs, case notes, or any relevant material. These are used as context and are not stored.'
        ),
        fields: [
          {
            name: 'contextFiles',
            type: 'fileUploader',
            // Empty label - the section is already titled "Context files"; a
            // field label under it would just say the same thing twice.
            label: '',
            gridSize: 'full',
            // Reference documents the AI engine can extract text from: documents,
            // spreadsheets, slides, structured data, and images (OCR). Keep in
            // sync with the backend ATTACHMENT_ALLOWED_EXTENSIONS whitelist.
            allowedExtensions: [
              'pdf', 'docx', 'txt', 'md',
              'csv', 'tsv', 'json', 'yaml', 'yml', 'xml',
              'xlsx', 'pptx',
              'png', 'jpg', 'jpeg', 'tiff', 'tif', 'bmp', 'webp',
            ],
          } as BaseFormFieldConfig,
        ],
      },
      {
        fields: [
          {
            name: '_launch_note',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: LaunchNote,
            customProps: { kindLabel },
          } as BaseFormFieldConfig,
        ],
      },
    ],
    [kindLabel, t]
  );

  // ── Initial values per step ───────────────────────────────────────
  const detailsInitial = useMemo(
    () => ({
      name:               data.name,
      description:        data.description,
      tone:               data.tone,
      instructions:       data.instructions,
      additional_context: data.additional_context,
    }),
    // Only recalculate when the user navigates back to this step (i.e. data changes from step 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep]
  );

  const contextInitial = useMemo(
    () => ({ contextFiles: data.contextFiles }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStep]
  );

  // ── New steps (template / matter / matter_context) ────────────────
  // Wrap each step component in a BaseForm so the Next/Back chrome stays
  // consistent. The components themselves are uncontrolled within the form —
  // state lives in `data` and changes are merged via setData.

  const matterSections: BaseFormSectionConfig[] = useMemo(() => [{
    fields: [{
      name: 'matter_uuid',
      type: 'custom',
      label: '',
      gridSize: 'full',
      customComponent: TieToMatterStep,
      customProps: {
        value: data.matter_uuid,
        onChange: (uuid: string, matter: MatterOption | null) =>
          setData((prev) => ({
            ...prev,
            matter_uuid: uuid,
            matter_jurisdiction: matter?.jurisdiction ?? null,
          })),
      },
    } as BaseFormFieldConfig],
  }], [data.matter_uuid]);

  const hasAttachments = data.contextFiles.length > 0;
  const matterContextSections: BaseFormSectionConfig[] = useMemo(() => [{
    fields: [{
      name: 'matter_context_options',
      type: 'custom',
      label: '',
      gridSize: 'full',
      customComponent: MatterContextStep,
      customProps: {
        matterUuid: data.matter_uuid,
        matterJurisdiction: data.matter_jurisdiction,
        hasAttachments,
        value: data.matter_context_options,
        onChange: (next: MatterContextOptions) =>
          setData((prev) => ({ ...prev, matter_context_options: next })),
      },
    } as BaseFormFieldConfig],
  }], [data.matter_uuid, data.matter_jurisdiction, data.matter_context_options, hasAttachments]);

  // ── Per-step BaseForm contract ────────────────────────────────────
  // Each step is a BaseForm with its own title + description rendered by
  // BaseForm itself (no `hideHeader`). Width is unified at `max-w-3xl` per
  // the platform convention. Validation that doesn't fit FormSchema (e.g.
  // "user must pick something from the catalogue") is surfaced via
  // submitDisabled + submitTooltip instead of post-submit feedback.

  const chooseStepLabel = t('Choose template');
  const matterStepLabel = t('Tie to a matter');
  const matterCtxStepLabel = t('Matter context');
  const detailsStepLabel = t('Document details');
  const contextStepLabel = t('Attachments & launch');

  const baseFormCommon = {
    maxWidth: WIZARD_MAX_WIDTH,
    showConfirmationModal: false as const,
  };

  const chooseSubmitDisabled = !data.document_type_id;
  const chooseSubmitTooltip = chooseSubmitDisabled
    ? t('Pick a document type or template to continue.')
    : undefined;

  // Back-button label: only the first step exits the wizard ("Cancel");
  // every later step steps back one ("Back"). handleBack already routes
  // idx 0 → onCancel, so this just keeps the label in sync.
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);
  const backLabel = currentIdx <= 0 ? t('Cancel') : t('Back');

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen overflow-hidden bg-bg">
      {/* Step rail - the shared wizard rail, same one the Matter wizard uses. */}
      <WizardStepRail steps={STEPS} currentStep={currentStep} maxWidth={WIZARD_MAX_WIDTH} />

      {/* Slim inline upload progress during the final create→upload→generate.
          The strip is reserved for the whole submit rather than mounted when
          the first file starts, so the body doesn't jump a row mid-flight; and
          an attachment upload is plain I/O, not an AI process, so it takes the
          neutral Spinner and not the logo loader (shared/LOADERS.md). */}
      {isSubmitting && (
        <div className="px-6 pt-1 flex-shrink-0">
          <div className={clsx('mx-auto flex h-5 items-center gap-1.5 text-xs font-medium text-fg-body', WIZARD_MAX_WIDTH)}>
            {uploadProgress && (
              <>
                <Spinner size="inline" className="shrink-0" />
                <span className="whitespace-nowrap">
                  {t('Uploading {{n}}/{{total}}', { n: uploadProgress.current, total: uploadProgress.total })}
                </span>
                <span className="truncate text-fg-muted">
                  {uploadProgress.name}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Step body - each step is a BaseForm following the platform header
          + section + footer layout. */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {currentStep === 'kind' && (
          <BaseForm
            key="step-kind"
            title={t('What are you creating?')}
            description={t('Choose whether to draft a document or build a strategy. You can set everything else up in the next steps.')}
            sections={kindSections}
            schema={kindSchema}
            initialValues={{}}
            onSubmit={handleKindStepSubmit}
            onBack={handleBack}
            backLabel={backLabel}
            submitLabel={t('Next')}
            submitDisabled={!kind}
            submitTooltip={!kind ? t('Pick a type to continue.') : undefined}
            {...baseFormCommon}
          />
        )}

        {currentStep === 'choose' && (
          <BaseForm
            key="step-choose"
            title={chooseStepLabel}
            description={t(
              'Pick either a PONS document type or one of your own structure templates. The same selection drives both the prompt and the section outline.',
            )}
            sections={chooseSections}
            schema={chooseSchema}
            initialValues={{}}
            onSubmit={handleChooseStepSubmit}
            onBack={handleBack}
            backLabel={backLabel}
            submitLabel={t('Next')}
            submitDisabled={chooseSubmitDisabled}
            submitTooltip={chooseSubmitTooltip}
            {...baseFormCommon}
          />
        )}

        {currentStep === 'matter' && (
          <BaseForm
            key="step-matter"
            title={matterStepLabel}
            description={t(
              'Optionally bind this {{kind}} to a matter so PONS can use that matter\'s context when generating.',
              { kind: kindLabel },
            )}
            sections={matterSections}
            schema={matterSchema}
            initialValues={{}}
            onSubmit={handleMatterStepSubmit}
            onBack={handleBack}
            backLabel={backLabel}
            submitLabel={t('Next')}
            {...baseFormCommon}
          />
        )}

        {currentStep === 'matter_context' && (
          <BaseForm
            key="step-matter-context"
            title={matterCtxStepLabel}
            description={t(
              'Toggle which slices of the bound matter\'s context PONS pulls into the prompt.',
            )}
            sections={matterContextSections}
            schema={matterContextSchema}
            initialValues={{}}
            onSubmit={handleMatterContextStepSubmit}
            onBack={handleBack}
            backLabel={backLabel}
            submitLabel={t('Next')}
            {...baseFormCommon}
          />
        )}

        {currentStep === 'details' && (
          <BaseForm
            key="step-details"
            title={detailsStepLabel}
            description={t(
              'Set a name and describe what the {{kind}} should cover. The more context you provide here, the better the output.',
              { kind: kindLabel },
            )}
            sections={detailsSections}
            schema={detailsSchema}
            initialValues={detailsInitial}
            onSubmit={handleDetailsStepSubmit}
            onBack={handleBack}
            backLabel={backLabel}
            submitLabel={t('Next')}
            {...baseFormCommon}
          />
        )}

        {currentStep === 'context' && (
          <BaseForm
            key="step-context"
            title={contextStepLabel}
            description={t(
              'Upload any reference documents the AI should consider, then launch generation. Large files are summarized server-side.',
            )}
            sections={contextSections}
            schema={contextSchema}
            initialValues={contextInitial}
            onSubmit={handleContextStepSubmit}
            onBack={handleBack}
            backLabel={backLabel}
            submitLabel={t('Create & generate')}
            isSubmitting={isSubmitting}
            {...baseFormCommon}
          />
        )}
      </div>
    </div>
  );
};

// ─── KindStep - Document vs Strategy selection (wizard step 0) ────────────────

const KIND_CHOICES: {
  value: DraftingKind;
  title: string;
  description: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
}[] = [
  {
    value: 'draft',
    title: 'Document',
    description: 'Generate a finished legal document - contracts, letters, memos, filings and more.',
    Icon: PiFileTextLight,
  },
  {
    value: 'strategy',
    title: 'Strategy',
    description: 'Build a strategic analysis - arguments, risk assessments, negotiation plans and insights.',
    Icon: PiLightbulbLight,
  },
];

const KindStep: React.FC<{
  value: DraftingKind | null;
  onChange: (kind: DraftingKind) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {KIND_CHOICES.map(({ value: v, title, description, Icon }) => {
        const selected = value === v;
        return (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={clsx(
              'flex flex-col items-start gap-3 rounded-xl border p-5 text-start transition-all duration-200',
              FOCUS_RING,
              selectedCardClass(selected),
            )}
          >
            <span
              className={clsx(
                'flex h-11 w-11 items-center justify-center rounded-lg transition-colors',
                selected
                  ? 'bg-champagne/10 text-champagne'
                  : 'bg-surface-sunken text-fg-muted',
              )}
            >
              <Icon size={22} />
            </span>
            <div>
              <p className="text-sm font-medium text-fg-strong">{t(title)}</p>
              <p className="mt-1 text-xs leading-relaxed text-fg-muted">
                {t(description)}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ─── LaunchNote - informational note rendered in step 3 ──────────────────────

const LaunchNote: React.FC<{ kindLabel: string }> = ({ kindLabel }) => {
  const { t } = useTranslation();
  return (
    <Well className="flex items-start gap-3 !p-4 text-sm">
      <PiRocketLaunchLight size={18} className="mt-0.5 shrink-0 text-fg-muted" />
      <div className="space-y-1">
        {/* Interpolated, not an interpolated KEY: `t(\`… ${kindLabel}\`)` builds
            a different lookup string per kind, so neither ever matches a
            catalogue entry and both fall through untranslated. */}
        <p className="font-medium text-fg-strong">
          {t('Ready to create your {{kind}}', { kind: kindLabel })}
        </p>
        <p className="text-xs leading-relaxed text-fg-muted">
          {t(
            'Generation starts immediately. Once complete, the document opens in the editor where you can review, refine, and regenerate sections as needed.'
          )}
        </p>
      </div>
    </Well>
  );
};

export default DraftingWizard;
